import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { adminApi, apiError } from "@/lib/api";
import { eur2 } from "@/lib/product";

export const REASONS = {
  demande_client: "Demande client",
  retractation: "Rétractation 14 jours",
  defectueux: "Produit défectueux",
  geste_commercial: "Geste commercial",
  autre: "Autre",
};

export const paidAmount = (o) => o.stripe_amount_total ?? o.total;
export const remainingAmount = (o) => Math.round((paidAmount(o) - (o.refunded_total || 0)) * 100) / 100;

const ModeChoice = ({ mode, setMode }) => (
  <div className="grid grid-cols-2 gap-2">
    {[["total", "Remboursement total"], ["partial", "Partiel"]].map(([k, l]) => (
      <button key={k} type="button" data-testid={`refund-mode-${k}`} onClick={() => setMode(k)}
        className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${mode === k ? "border-sky-600 bg-sky-50 text-sky-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
        {l}
      </button>
    ))}
  </div>
);

export const RefundDialog = ({ order, open, onClose, onDone }) => {
  const remaining = remainingAmount(order);
  const first = !(order.refunded_total > 0);
  const [mode, setMode] = useState("total");
  const [amount, setAmount] = useState("");
  const [withShipping, setWithShipping] = useState(true);
  const [reason, setReason] = useState("demande_client");
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("total"); setAmount(""); setWithShipping(true); setReason("demande_client"); setNote(""); setNotify(true); setConfirm(false); setError("");
  }, [open]);

  const value = mode === "total"
    ? (withShipping || !first ? remaining : Math.round((remaining - order.shipping_fee) * 100) / 100)
    : Number(String(amount).replace(",", "."));

  const next = () => {
    if (!(value > 0)) return setError("Indiquez un montant supérieur à 0 €.");
    if (value > remaining + 0.001) return setError(`Montant maximum remboursable : ${eur2(remaining)}.`);
    if (reason === "autre" && !note.trim()) return setError("Précisez le motif dans la note.");
    setError(""); setConfirm(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const body = { mode, reason, note: note.trim() || null, notify, include_shipping: withShipping, amount: mode === "partial" ? value : null };
      const { data } = await adminApi.post(`/orders/${order.order_id}/refund`, body);
      toast.success(`Remboursement de ${eur2(data.refund.amount)} ${data.refund.status === "succeeded" ? "effectué" : "en cours"}`);
      if (data.email && !data.email.ok) toast.error(`E-mail client non envoyé : ${data.email.error}`);
      onDone(data.order);
    } catch (e) {
      toast.error(apiError(e)); setConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="refund-dialog" className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Rembourser la commande {order.order_id}</DialogTitle>
          <DialogDescription data-testid="refund-remaining">Encore remboursable : {eur2(remaining)} (payé {eur2(paidAmount(order))})</DialogDescription>
        </DialogHeader>
        {!confirm ? (
          <div className="flex flex-col gap-4">
            <ModeChoice mode={mode} setMode={(m) => { setMode(m); setError(""); }} />
            {mode === "total" ? (
              first && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" data-testid="refund-include-shipping" checked={withShipping} onChange={(e) => setWithShipping(e.target.checked)} />
                  Inclure les frais de livraison ({eur2(order.shipping_fee)})
                </label>
              )
            ) : (
              <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">Montant (€)
                <input data-testid="refund-amount-input" inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value); setError(""); }} className="field-input" placeholder={`max ${remaining.toFixed(2).replace(".", ",")}`} />
              </label>
            )}
            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">Motif
              <select data-testid="refund-reason-select" value={reason} onChange={(e) => setReason(e.target.value)} className="field-input">
                {Object.entries(REASONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">Note {reason === "autre" ? "(obligatoire)" : "(facultatif)"}
              <textarea data-testid="refund-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="field-input" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="refund-notify-checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
              Envoyer un e-mail au client
            </label>
            {error && <p data-testid="refund-error" role="alert" className="text-sm text-red-600">{error}</p>}
            <button type="button" onClick={next} data-testid="refund-next-button" className="btn-heat !py-2.5">Continuer</button>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="refund-confirm-step">
            <p className="text-sm text-slate-700">
              Montant : <strong data-testid="refund-confirm-amount">{eur2(value)}</strong> · Motif : {REASONS[reason]}{note && ` – ${note}`}
              <br />Le remboursement est envoyé sur le moyen de paiement d'origine ({order.payment_method === "klarna" ? "Klarna" : "carte"}). Cette action est irréversible.
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setConfirm(false)} disabled={saving} data-testid="refund-back-button" className="btn-ghost-ice !py-2.5">Retour</button>
              <button type="button" onClick={submit} disabled={saving} data-testid="refund-confirm-button" className="btn-heat !py-2.5 flex-1 disabled:opacity-50">
                {saving ? "Remboursement…" : `Confirmer le remboursement de ${eur2(value)}`}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const RefundList = ({ order }) => (
  <div data-testid="order-detail-refunds" className="rounded-xl bg-rose-50 border border-rose-100 p-4 text-sm">
    <span className="font-semibold text-slate-800">Remboursements — total {eur2(order.refunded_total || 0)}</span>
    <ul className="mt-2 flex flex-col gap-1.5">
      {order.refunds.map((r) => (
        <li key={r.id} data-testid={`refund-item-${r.id}`} className="flex justify-between gap-3 text-xs">
          <span>{new Date(r.at).toLocaleString("fr-FR")} · {r.reason}{r.note ? ` – ${r.note}` : ""}{r.source === "stripe" ? " (Stripe)" : ""}</span>
          <span className="font-semibold whitespace-nowrap">{eur2(r.amount)} · {{ succeeded: "effectué", pending: "en cours", failed: "échoué", canceled: "annulé" }[r.status] || r.status}</span>
        </li>
      ))}
    </ul>
  </div>
);
